import { MenuItem } from "@/app/ui/navigation/types";
import { EnumAlcoholType } from "@/app/services/generated/enums/enumAlcoholType.generated";
import { LocalDate } from "@js-joda/core";
import type { Location } from "vue-router";
import type Guid from "@/app/types/common/guid";
import {
  dutySchemeType,
  useDutyRulesForHeaders,
} from "@/components/pages/duty/composables/dutyConfiguration";
import { EnumUserPermission } from "@/app/services/generated/enums/enumUserPermission.generated";
import { hasPermissionsUsingUserStore } from "@/components/pages/administration/user_roles/UserAccessHandler";

const { dutyRules } = useDutyRulesForHeaders();

export const dutyMenuGerman = {
  root: "navigation_duty_group_german",
  beerDuty: {
    root: "navigation_beer_duty_group",
    dutyReturnList: "german_duty_beer_duty_return_list",
    dutyReturnListLocation: (): Location => ({
      name: dutyMenuGerman.beerDuty.dutyReturnList,
    }),
    editReturn: "german_duty_beer_duty_edit_return",
    editReturnLocation: (id: Guid, isEditing: boolean): Location => ({
      name: dutyMenuGerman.beerDuty.editReturn,
      params: {
        id: id.toString(),
        mode: isEditing ? "edit" : "view",
      },
    }),
    createReturn: "german_duty_beer_duty_create_return",
    createReturnLocation: (): Location => ({
      name: dutyMenuGerman.beerDuty.createReturn,
    }),
    audit: "german_duty_beer_duty_audit_report",
    viewAudit: (fromDate: LocalDate, toDate: LocalDate): Location => ({
      name: dutyMenuGerman.beerDuty.audit,
      params: {
        fromDateInclusive: fromDate.toString(),
        toDateInclusive: toDate.toString(),
      },
    }),
  },
};

export const buildDutyMenuGermanItems: () => MenuItem[] = () => [
  MenuItem.forBranch({
    name: dutyMenuGerman.root,
    icon: "Assignment",
    visible: () =>
      dutySchemeType.value.isGermany() &&
      hasPermissionsUsingUserStore([EnumUserPermission.menuDutyView]),
    subMenus: [
      MenuItem.fromOpts({
        name: dutyMenuGerman.beerDuty.dutyReturnList,
        url: "/duty/german/beerreturn/list",
        breadcrumbParentName: dutyMenuGerman.beerDuty.root,
        // german duty is enabled if movement type is shown
        visible: () => dutyRules.value.germanBeerDutyMovementType.show,
        view: () =>
          import(
            /* webpackChunkName: "duty" */ "@/components/pages/duty/german/ListGermanBeerDutyReturns.vue"
          ),
      }),
      MenuItem.fromOpts({
        name: dutyMenuGerman.beerDuty.editReturn,
        url: "/duty/german/beerreturn/:id/:mode",
        breadcrumbParentName: dutyMenuGerman.beerDuty.dutyReturnList,
        view: () =>
          import(
            /* webpackChunkName: "distribution" */ "@/components/pages/duty/german/EditGermanBeerDutyReturn.vue"
          ),
        visible: false,
        componentProps: (route) => {
          return {
            id: route.params.id,
            editing:
              route.params.id &&
              route.params.mode.toLowerCase().trim() === "edit",
          };
        },
      }),
      MenuItem.fromOpts({
        name: dutyMenuGerman.beerDuty.createReturn,
        url: "/duty/german/beerreturn/create",
        breadcrumbParentName: dutyMenuGerman.beerDuty.dutyReturnList,
        view: () =>
          import(
            /* webpackChunkName: "distribution" */ "@/components/pages/duty/german/EditGermanBeerDutyReturn.vue"
          ),
        visible: false,
        componentProps: (route) => ({
          editing: true,
        }),
      }),
      MenuItem.fromOpts({
        name: dutyMenuGerman.beerDuty.audit,
        url: "/duty/german/audit",
        componentProps: (r) => ({
          fromDateInclusive:
            r.params.fromDateInclusive &&
            LocalDate.parse(r.params.fromDateInclusive),
          toDateInclusive:
            r.params.toDateInclusive &&
            LocalDate.parse(r.params.toDateInclusive),
          alcoholType: [EnumAlcoholType.beer],
        }),
        view: () => import("@/components/pages/duty/de/DutyAudit.vue"),
        visible: () => dutyRules.value.germanBeerDutyMovementType.show,
      }),
    ],
  }),
];
